import {
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
} from 'react'

import cn from 'classnames'
import { format, Locale } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { getPreviewContent } from '@app/api'

import { ChatContext } from '@app/context'
import {
  getBaseInfo,
  getDateFnsLocale,
  getIsB2BOrCurrentUserPrivateAdvertiser,
  shouldFallbackToAccount,
} from '@app/helpers'
import { useTranslatedMessage, useModal, useUser } from '@app/hooks'
import { extractLinks, getLocalDate, isB2BView } from '@app/utils'

import {
  Avatar,
  ConfirmMessage,
  HtmlText,
  LinkPreview,
  LinkPreviewData,
  Modal,
} from '@lib/components'

import styles from './styles.module.scss'
import { MessageItemProps } from './types'
import { DialogListingCard } from '../ListingCard'
import {
  MessageFileAttachments,
  MessageImageAttachments,
  MessageListingAttachments,
  useFileAttachments,
  useImageAttachments,
  useListingAttachments,
} from '../MessageAttachments'
import { ContextMenu } from './ContextMenu'
import { MessageSpamIndicator } from './MessageSpamIndicator'
import { getListingIdsFromLinks, splitListingLinks } from '../MessageAttachments/MessageListingAttachments/listingLinkUtils'
import { MessageEmailContentContainer } from '../MessageEmailContent'

export const MessageItem = ({
  message,
  conversation,
  abortController,
  openMenuId,
  setOpenMenuId,
  isStaff,
}: MessageItemProps) => {
  const { state } = use(ChatContext)
  const userState = useUser()
  const { t, i18n } = useTranslation()
  const locale = getDateFnsLocale(i18n.language)
  const translatedMessage = useTranslatedMessage(message, conversation)

  const messageLinks = useMemo(
    () => extractLinks(message.content?.text),
    [message.content?.text]
  )

  const { otherLinks } = useMemo(
    () => splitListingLinks(messageLinks),
    [messageLinks]
  )

  const listingIdsFromLinks = useMemo(
    () => getListingIdsFromLinks(messageLinks),
    [messageLinks]
  )

  const previewLink = useMemo(() => {
    if (!otherLinks.length) {
      return null
    }

    return otherLinks[otherLinks.length - 1]
  }, [otherLinks])

  const listingAttachmentItems = useListingAttachments({
    attachments: message.attachments,
    conversation,
    listingIdsFromLinks,
  })

  const imageAttachmentItems = useImageAttachments({
    imageAttachments: message.images,
    attachments: message.attachments,
  })

  const fileAttachmentItems = useFileAttachments({
    attachments: message.attachments,
  })

  const formatTime = format(getLocalDate(message.creationDate), 'HH:mm', {
    locale,
  })

  const emailDetailsModal = useModal(`emailDetailsModal_${message.id}`)
  const [preview, setPreview] = useState<LinkPreviewData | null>(null)

  const dialogBlockClassName = cn(styles.dialogblock, {
    [styles.queued]:
      message.sendStatus == 'Queued' || message.sendStatus == 'Rejected',
  })

  const messageParticipant = useMemo(
    () =>
      conversation.participants?.find(
        (item) => item.id === message.fromParticipantId
      ) || null,
    [conversation.participants, message.fromParticipantId]
  )

  const { avatar, fullName } = useMemo(() => {
    if (!messageParticipant) {
      return { fullName: '', avatar: null }
    }

    return getBaseInfo(state, messageParticipant, conversation)
  }, [conversation, messageParticipant, state])

  const shouldUseLogoTemplate = shouldFallbackToAccount(
    messageParticipant,
    conversation
  )

  const avatarTemplate = shouldUseLogoTemplate ? 'logo-sm' : 'user-sm'

  const formattedTime = useMemo(
    () => formatTimeAndDate(message.creationDate, locale),
    [message.creationDate, locale]
  )

  const handleViewEmailClick = useCallback((e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation()
    emailDetailsModal.open()
  }, [])
  const RepliedByEmail = () => {
    return (
      <div className={styles.repliedByEmail}>
        <span className={styles.muted}>{t('Chat.Message.RepliedByEmail')}</span>
        <span className={styles.viewEmail} onClick={handleViewEmailClick}>
          {t('Chat.Message.ViewEmail')}
        </span>
      </div>
    )
  }

  const listingId =
    message.metadata?.listingId ||
    message.metadata?.unitId ||
    message.metadata?.projectId

  const isB2B = Boolean(isB2BView())
  const isB2BOrCurrentUserPrivateAdvertiser =
    getIsB2BOrCurrentUserPrivateAdvertiser(
      conversation.participants,
      userState.currentUser?.id,
      isB2B
    )

  const fetchRemotePreviewContent = useCallback(
    async (link: string): Promise<LinkPreviewData | null> => {
      if (!link) {
        return null
      }

      const res = await getPreviewContent(link)

      if (!res) {
        return null
      }

      return {
        url: link,
        ...res,
      }
    },
    []
  )

  useEffect(() => {
    let cancelled = false

    if (!previewLink) {
      setPreview(null)
      return
    }

    setPreview(null)

    const loadPreview = async () => {
      const data = await fetchRemotePreviewContent(previewLink)
      if (!cancelled) {
        setPreview(data)
      }
    }

    void loadPreview()

    return () => {
      cancelled = true
    }
  }, [fetchRemotePreviewContent, previewLink])

  return (
    <>
      <div className={dialogBlockClassName}>
        <div className={styles.avatar}>
          <Avatar
            avatar={avatar}
            fullName={fullName}
            size="small"
            template={avatarTemplate}
          />
        </div>
        <div className={styles.message}>
          <div className={styles.messagetop}>
            <div className={styles.fullName}>{fullName}</div>
            <div className={styles.date}>{formatTime}</div>
          </div>
          <div className={styles.body}>
            <HtmlText body={translatedMessage} />
            <MessageImageAttachments items={imageAttachmentItems} />
            <MessageFileAttachments items={fileAttachmentItems} />
            <MessageListingAttachments items={listingAttachmentItems} />
            {preview && <LinkPreview data={preview} />}
            {message.source === 'Email' && <RepliedByEmail />}
          </div>
          {listingId && (
            <DialogListingCard
              listingId={listingId}
              isTourRequest={message.metadata?.leadType === 'Tour'}
              tourDate={message.metadata?.tourDate}
              tourTime={message.metadata?.tourTime}
            />
          )}
          {isStaff && message.state == 'Spam' && <MessageSpamIndicator />}
        </div>
        {isStaff && (
          <div className={styles.contextMenuWrapper}>
            <ContextMenu
              conversationId={conversation.id}
              message={message}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              abortController={abortController}
            />
          </div>
        )}
      </div>
      {message.metadata?.leadType === 'Tour' &&
        isB2BOrCurrentUserPrivateAdvertiser && (
        <ConfirmMessage
          message={t('Chat.Inbox.Confirmation', {
            fullname: fullName,
          })}
        />
      )}

      <Modal
        isOpen={emailDetailsModal.isOpen}
        onClose={emailDetailsModal.close}
        title={t('Chat.Message.EmailDetails', {
          fullName: fullName,
          date: formattedTime,
        })}
      >
        <MessageEmailContentContainer
          conversation={conversation}
          message={message}
          abortController={abortController}
        />
      </Modal>
    </>
  )
}

const formatTimeAndDate = (dateStr: string, locale: Locale): string => {
  const date = new Date(dateStr)
  return format(getLocalDate(date), 'HH:mm dd MMMM yyyy', {
    locale,
  })
}
